import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";
import EachText from "./wrapper/EachText";
import HoverTextNearMouse from "./components/HoverTextNearMouse";
import GenericChartComponent from "../GenericChartComponent";
import { getMouseCanvas } from "../GenericComponent";

class InteractiveText extends Component {
  constructor(props) {
    super(props);

    this.handleDraw = this.handleDraw.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.terminate = terminate.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType("textList").bind(this);

    this.nodes = [];
    this.state = {};
    this.timeoutId = null;
  }

  componentWillUnmount() {
    // Clear any pending timeout to prevent memory leaks
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  handleDrag(index, position) {
    this.setState({
      override: {
        index,
        position,
      },
    });
  }
  handleDragComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { textList } = this.props;
      const newTextList = textList.map((each, idx) => {
        const selected = idx === override.index;
        return selected
          ? {
              ...each,
              position: override.position,
              selected,
            }
          : {
              ...each,
              selected,
            };
      });
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onDragComplete(newTextList, moreProps);
        },
      );
    }
  }
  handleDrawLine(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.start)) {
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }
  handleDraw(moreProps, e) {
    const { enabled } = this.props;
    if (enabled) {
      const {
        mouseXY: [, mouseY],
        chartConfig: { yScale },
        xAccessor,
        currentItem,
      } = moreProps;

      const xyValue = [xAccessor(currentItem), yScale.invert(mouseY)];

      const { defaultText, onChoosePosition } = this.props;

      // Use the current appearance (which includes the drawing color from InteractiveLayer)
      // instead of fetching from registry which would override the selected color
      const { appearance } = this.props;
      const currentAppearance = appearance;

      // Store appearance in nested format to match other interactive objects
      const newText = {
        position: xyValue,
        text: defaultText.text,
        // Store all appearance properties in the appearance object
        appearance: {
          ...currentAppearance,
          // Ensure text property is included
          text: defaultText.text,
        },
      };

      // Create array with all existing text items plus the new one (like TrendLine does)
      const { textList = [] } = this.props;
      const newTextIndex = textList.length; // Index of the new text item
      const newTextList = [
        ...textList.map((d) => ({ ...d, selected: false })),
        {
          ...newText,
          selected: true,
        },
      ];

      onChoosePosition(newTextList, moreProps, e);

      // Auto-open edit modal for InteractiveText
      const { onOpenEditModal, mainChartId } = this.props;
      if (onOpenEditModal) {
        // Use setTimeout to ensure the state is updated first
        this.timeoutId = setTimeout(() => {
          const { chartId } = this.context; // This is the chart section ID (1, 2, 3, etc.)
          const actualMainChartId = mainChartId || "default"; // This is the main chart store ID
          const interactiveId = `${actualMainChartId}_${chartId}_interactiveText_${newTextIndex}`;
          onOpenEditModal(interactiveId);
          this.timeoutId = null; // Clear the reference
        }, 50);
      }
    } /*  else {
			this.handleClick(moreProps, e);
		} */
  }
  render() {
    const { textList, defaultText, hoverText, appearance } = this.props;
    const { override } = this.state;

    return (
      <g>
        {textList.map((each, idx) => {
          const defaultHoverText = InteractiveText.defaultProps.hoverText;

          // Merge individual appearance like TrendLine does
          let eachAppearance = appearance;
          if (each.appearance) {
            eachAppearance = { ...appearance, ...each.appearance };
            // For InteractiveText, prioritize individual font colors
            if (each.appearance.font) {
              eachAppearance.font = {
                ...(appearance.font || {}),
                ...each.appearance.font,
              };
            }
            if (each.appearance.stroke) {
              eachAppearance.stroke = {
                ...(appearance.stroke || {}),
                ...each.appearance.stroke,
              };
            }
          }

          // Use registry format directly with proper extraction
          const getFont = (appearance) => {
            if (appearance.font && typeof appearance.font === "object") {
              // Priority: font.color (user's choice) > default
              const fontColor = appearance.font.color;
              const textFill = fontColor || "#000000";

              return {
                fontSize: appearance.font.fontSize || 12,
                fontFamily:
                  appearance.font.fontFamily ||
                  "Helvetica Neue, Helvetica, Arial, sans-serif",
                textFill: textFill,
                fontWeight: "normal",
                fontStyle: "normal",
              };
            }
            return {
              fontSize: 12,
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              textFill: "#000000",
              fontWeight: "normal",
              fontStyle: "normal",
            };
          };

          const getBgFill = (appearance) => {
            if (appearance.bgFill && typeof appearance.bgFill === "object") {
              return {
                bgFill: appearance.bgFill.color || "#FFFFFF",
                bgOpacity:
                  appearance.bgFill.opacity !== undefined
                    ? appearance.bgFill.opacity
                    : 0.9,
              };
            }
            return {};
          };

          const getBgStroke = () => {
            return {};
          };

          // Extract properties from all sources
          const defaultProps = {
            ...getFont(defaultText),
            ...getBgFill(defaultText),
            ...getBgStroke(defaultText),
            text: defaultText.text || "Lorem ipsum...", // Add text property
          };

          const eachProps = {
            ...getFont(each),
            ...getBgFill(each),
            ...getBgStroke(each),
            text: each.text, // Add text property
          };

          const appearanceProps = each.appearance
            ? {
                ...getFont(eachAppearance),
                ...getBgFill(each.appearance),
                ...getBgStroke(each.appearance),
                // Text can also be edited in appearance - extract the actual text value
                text:
                  each.appearance.text &&
                  typeof each.appearance.text === "object" &&
                  each.appearance.text.text
                    ? each.appearance.text.text
                    : each.appearance.text,
              }
            : {};

          // Merge appearance with correct priority: individual item appearance > item base > default
          const props = {
            ...defaultProps,
            ...eachProps,
            ...appearanceProps, // This should override everything else
            // Final fallback for text - ensure it's always a string
            text: String(
              appearanceProps.text ||
                each.text ||
                defaultText.text ||
                "Lorem ipsum...",
            ),
            hoverText: {
              ...defaultHoverText,
              ...hoverText,
              ...(each.hoverText || {}),
            },
          };

          return (
            <EachText
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...props}
              selected={each.selected}
              position={getValueFromOverride(
                override,
                idx,
                "position",
                each.position,
              )}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              edgeInteractiveCursor="react-stockcharts-move-cursor"
            />
          );
        })}
        <GenericChartComponent
          onClick={this.handleDraw}
          svgDraw={noop}
          canvasDraw={noop}
          canvasToDraw={getMouseCanvas}
          drawOn={["mousemove", "pan", "xAxisZoom"]}
          data-evo-id="interactive-interactive-text"
        />
        ;
      </g>
    );
  }
}

InteractiveText.propTypes = {
  onChoosePosition: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  onOpenEditModal: PropTypes.func,
  mainChartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  defaultText: PropTypes.shape({
    bgFill: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ color: PropTypes.string, opacity: PropTypes.number }),
    ]).isRequired,
    bgOpacity: PropTypes.number.isRequired,
    bgStrokeWidth: PropTypes.number,
    bgStroke: PropTypes.string,
    textFill: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ color: PropTypes.string }),
    ]).isRequired,
    fontFamily: PropTypes.string.isRequired,
    fontWeight: PropTypes.string.isRequired,
    fontStyle: PropTypes.string.isRequired,
    fontSize: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,

  hoverText: PropTypes.object.isRequired,
  textList: PropTypes.array.isRequired,
  enabled: PropTypes.bool.isRequired,
  appearance: PropTypes.object,
};

InteractiveText.defaultProps = {
  onChoosePosition: noop,
  onDragComplete: noop,
  onSelect: noop,
  onOpenEditModal: noop,

  defaultText: {
    bgFill: "#D3D3D3",
    bgOpacity: 0,
    bgStrokeWidth: 1,
    textFill: "#F10040",
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: "normal",
    text: "Lorem ipsum...",
  },
  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
  textList: [],
};

InteractiveText.contextTypes = {
  subscribe: PropTypes.func.isRequired,
  unsubscribe: PropTypes.func.isRequired,
  generateSubscriptionId: PropTypes.func.isRequired,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default InteractiveText;
